import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getMessageFromAPIError as getMessageFromCampaignAPIError } from 'campaigns-lib/utils/campaignName';
import useSaveCrmObject from 'framework-asset-preview-lib/internal/shared/hooks/useSaveCrmObject';
import { alertFailure, alertSuccess } from 'framework-asset-preview-lib/internal/shared/utils/alerts';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import useAssetPreviewTracking from 'framework-asset-preview-lib/internal/shared/hooks/useAssetPreviewTracking';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { useAssetPreview } from './AssetPreviewProvider';
import { usePreviewData } from './DataProvider';
import { isConflictingUTMError } from '../../../utils/error';
import { useFetchUTMs } from '../../objectTypes/0-35/api';
import useCrmObject from '../hooks/useCrmObject';
import { CAMPAIGN_PROPERTIES } from '../constants/campaignProperties';
import { loadSurvey } from '../utils/survey';
import { EDITABLE_UTM_SURVEY_ID } from '../constants/survey';
import { jsx as _jsx } from "react/jsx-runtime";
const InlineEditContext = /*#__PURE__*/createContext(undefined);
export default function InlineEditProvider({
  children
}) {
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();
  const {
    refetchCrmObject
  } = usePreviewData();
  const {
    onUpdateCrmObject,
    objectType
  } = useAssetPreview();
  const [formState, setFormState] = useState({});
  const [validationState, setValidationState] = useState();
  const {
    isUngatedFor
  } = useUserInfo();
  const isUngatedForEditableUTM = useMemo(() => isUngatedFor('MO:Campaigns:EditableUTMs'), [isUngatedFor]);
  const isDraft = useMemo(() => Object.keys(formState).length > 0, [formState]);
  const [currentFormStateSaveError, setCurrentFormStateSaveError] = useState(false);
  const {
    saveCrmObject,
    isSaving
  } = useSaveCrmObject();
  const handleResetValue = useCallback(propertyName => {
    setCurrentFormStateSaveError(false);
    setFormState(prev => {
      const rest = _objectWithoutPropertiesLoose(prev, [propertyName].map(_toPropertyKey));
      return rest;
    });
  }, []);
  const handleReset = useCallback(() => {
    setCurrentFormStateSaveError(false);
    setFormState({});
  }, []);
  const handleChange = useCallback(changes => {
    setCurrentFormStateSaveError(false);
    setFormState(prev => Object.assign({}, prev, changes));
  }, []);
  const handleValidationChange = useCallback(state => setValidationState(state), []);
  const crmObject = useCrmObject();
  const {
    refetch: refetchUTMs
  } = useFetchUTMs(crmObject);
  const handleSave = useCallback(() => {
    const isUTMUpdated = (CAMPAIGN_PROPERTIES.UTM in formState);
    const isNameUpdated = (CAMPAIGN_PROPERTIES.NAME in formState);
    return saveCrmObject(formState).then(updatedCrmObject => {
      trackAssetPreviewInteraction({
        action: 'inline edit success',
        numberOfProperties: Object.keys(formState).length,
        isUTMUpdated,
        editedProperties: Object.keys(formState)
      });
      alertSuccess({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkAssetPreviewLib.inlineEdit.changesSaved"
        })
      });
      setFormState({});
      refetchCrmObject(updatedCrmObject);
      if (isNameUpdated || isUTMUpdated) {
        refetchUTMs();
      }
      if (isUTMUpdated) {
        loadSurvey('CSAT', EDITABLE_UTM_SURVEY_ID);
      }
      if (onUpdateCrmObject && updatedCrmObject) {
        onUpdateCrmObject(updatedCrmObject);
      }
    }).catch(error => {
      setCurrentFormStateSaveError(true);
      trackAssetPreviewInteraction({
        action: 'inline edit failure',
        numberOfProperties: Object.keys(formState).length,
        isUTMUpdated
      });
      switch (objectType) {
        case OBJECT_TYPE.CAMPAIGN:
          {
            const titleKey = isUngatedForEditableUTM && isConflictingUTMError(error) ? 'campaignUTMUpdateFailed' : 'campaignUpdateFailed';
            alertFailure({
              message: error instanceof Error ? getMessageFromCampaignAPIError(error) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "frameworkAssetPreviewLib.inlineEdit.changesError"
              }),
              title: I18n.text(`campaignsLib.errors.${titleKey}.title`)
            });
            break;
          }
        default:
          alertFailure({
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "frameworkAssetPreviewLib.inlineEdit.changesError"
            })
          });
      }
    });
  }, [formState, objectType, onUpdateCrmObject, refetchCrmObject, saveCrmObject, trackAssetPreviewInteraction, isUngatedForEditableUTM, refetchUTMs]);
  const getValue = useCallback((propertyName, defaultValue) => {
    return propertyName in formState ? formState[propertyName] || '' : defaultValue;
  }, [formState]);
  const value = useMemo(() => ({
    formState,
    getValue,
    hasErrors: currentFormStateSaveError || !!(validationState && validationState.errors.length > 0),
    isDraft,
    isLoading: isSaving || !!(validationState && validationState.loading),
    isSaving,
    onChange: handleChange,
    onReset: handleReset,
    onResetValue: handleResetValue,
    onSave: handleSave,
    onValidationChange: handleValidationChange
  }), [currentFormStateSaveError, formState, getValue, handleChange, handleReset, handleResetValue, handleSave, handleValidationChange, isDraft, isSaving, validationState]);
  return /*#__PURE__*/_jsx(InlineEditContext.Provider, {
    value: value,
    children: children
  });
}
export function useInlineEdit() {
  const context = useContext(InlineEditContext);
  if (context === undefined) {
    throw new Error('useInlineEdit must be used within a InlineEditProvider');
  }
  return context;
}